import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Section from './Section';
import Endpoint from './vEndpoint';
import './APIDocumentation.css';

const APIDocumentation = () => {
  const [activeId, setActiveId] = useState('introduction');

  const navigateTo = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveId(id);
    }
  };

  const endpoints = [
    {
      method: 'GET',
      path: '/courses',
      description: 'Retrieve a summary of courses with optional filters for course level and department ID.',
      queryParams: [
        { name: 'course_level', type: 'String', required: false, description: 'Filter by course level (e.g., Beginner)' },
        { name: 'dept_id', type: 'Integer', required: false, description: 'Filter by department ID' },
      ],
      response: { status: '200 OK', content: 'JSON array of course summaries' },
      exampleRequest: 'curl -X GET "https://crlndportal-backend-dev.salmonground-e485a14f.eastus.azurecontainerapps.io/api/v1/courses?course_level=Beginner&dept_id=1"',
      exampleResponse: [
        {
          course_id: 1,
          course_title: 'Introduction to Python',
          course_description: 'Learn Python basics',
          course_level: 'Beginner',
          department_name: 'Computer Science',
          enrolled_user_count: 50,
          course_category: 'Programming',
          video_count: 10,
          total_duration: '2 hrs 30 mins',
        },
        {
          course_id: 2,
          course_title: 'Data Structures',
          course_description: 'Explore data structures',
          course_level: 'Intermediate',
          department_name: 'Computer Science',
          enrolled_user_count: 30,
          course_category: 'Programming',
          video_count: 15,
          total_duration: '3 hrs 45 mins',
        },
      ],
    },
    {
      method: 'GET',
      path: '/courses/{course_id}/playlist',
      description: 'Retrieve playlist details for a specific course by its ID.',
      pathParams: [{ name: 'course_id', type: 'Integer', required: true, description: 'The ID of the course' }],
      response: { status: '200 OK', content: 'JSON object with course playlist details' },
      exampleRequest: 'curl -X GET "https://crlndportal-backend-dev.salmonground-e485a14f.eastus.azurecontainerapps.io/api/v1/courses/1/playlist"',
      exampleResponse: {
        course_id: 1,
        title: 'Introduction to Python',
        description: 'Learn Python basics',
        playlist_id: 'PL123456789',
        course_level: 'Beginner',
      },
    },
    {
      method: 'GET',
      path: '/courses/{course_id}/materials',
      description: 'Retrieve all materials for a specific course by its ID.',
      pathParams: [{ name: 'course_id', type: 'Integer', required: true, description: 'The ID of the course' }],
      response: { status: '200 OK', content: 'JSON array of course materials' },
      exampleRequest: 'curl -X GET "https://crlndportal-backend-dev.salmonground-e485a14f.eastus.azurecontainerapps.io/api/v1/courses/1/materials"',
      exampleResponse: [
        {
          material_id: 1,
          course_id: 1,
          title: 'Python Variables',
          type: 'video',
          url: 'https://youtube.com/watch?v=abc123',
          video_id: 'abc123',
          duration: '5:30',
          order_index: 1,
        },
        {
          material_id: 2,
          course_id: 1,
          title: 'Python Loops',
          type: 'video',
          url: 'https://youtube.com/watch?v=def456',
          video_id: 'def456',
          duration: '7:15',
          order_index: 2,
        },
      ],
    },
    {
      method: 'GET',
      path: '/users/{user_id}/courses',
      description: 'Retrieve all courses for a specific user by their ID.',
      pathParams: [{ name: 'user_id', type: 'Integer', required: true, description: 'The ID of the user' }],
      response: { status: '200 OK', content: 'JSON array of user courses' },
      exampleRequest: 'curl -X GET "https://crlndportal-backend-dev.salmonground-e485a14f.eastus.azurecontainerapps.io/api/v1/users/1/courses"',
      exampleResponse: [
        {
          course_id: 1,
          course_title: 'Introduction to Python',
          course_description: 'Learn Python basics',
          course_level: 'Beginner',
          department_name: 'Computer Science',
          enrollment_date: '2025-01-15T10:00:00',
          course_category: 'Programming',
          video_count: 10,
          total_duration: '2 hrs 30 mins',
        },
      ],
    },
    {
      method: 'GET',
      path: '/users/info',
      description: 'Retrieve user information by email address.',
      queryParams: [{ name: 'email', type: 'String', required: true, description: 'User email address' }],
      response: { status: '200 OK', content: 'JSON object with user details' },
      exampleRequest: 'curl -X GET "https://crlndportal-backend-dev.salmonground-e485a14f.eastus.azurecontainerapps.io/api/v1/users/info?email=user@example.com"',
      exampleResponse: {
        user_id: 1,
        email: 'user@example.com',
        department_id: 1,
        username: 'john_doe',
        is_admin: false,
        isManager: false,
      },
    },
    {
      method: 'GET',
      path: '/users/{user_id}/progress',
      description: 'Retrieve course progress for a specific user.',
      pathParams: [{ name: 'user_id', type: 'Integer', required: true, description: 'The ID of the user' }],
      response: { status: '200 OK', content: 'JSON array of course progress details' },
      exampleRequest: 'curl -X GET "https://crlndportal-backend-dev.salmonground-e485a14f.eastus.azurecontainerapps.io/api/v1/users/1/progress"',
      exampleResponse: [
        {
          course_id: 1,
          course_title: 'Introduction to Python',
          total_materials: 10,
          materials_started: 8,
          materials_completed: 6,
          progress_percent: 60,
        },
      ],
    },
    {
      method: 'GET',
      path: '/users/{user_id}/courses/{course_id}/completion',
      description: 'Retrieve material completion status for a specific user and course.',
      pathParams: [
        { name: 'user_id', type: 'Integer', required: true, description: 'The ID of the user' },
        { name: 'course_id', type: 'Integer', required: true, description: 'The ID of the course' },
      ],
      response: { status: '200 OK', content: 'JSON object with material completion status' },
      exampleRequest: 'curl -X GET "https://crlndportal-backend-dev.salmonground-e485a14f.eastus.azurecontainerapps.io/api/v1/users/1/courses/1/completion"',
      exampleResponse: { 1: true, 2: false, 3: true },
    },
    {
      method: 'GET',
      path: '/users/{user_id}/insights',
      description: 'Retrieve insights for a specific user, including enrolled courses, completed courses, and time spent.',
      pathParams: [{ name: 'user_id', type: 'Integer', required: true, description: 'The ID of the user' }],
      response: { status: '200 OK', content: 'JSON object with user insights' },
      exampleRequest: 'curl -X GET "https://crlndportal-backend-dev.salmonground-e485a14f.eastus.azurecontainerapps.io/api/v1/users/1/insights"',
      exampleResponse: {
        enrolled_courses: 5,
        completed_courses: 2,
        in_progress_courses: 3,
        certificates_earned: 2,
        total_time_spent: '10 hrs 45 mins',
        completion_rate: 40.0,
      },
    },
    {
      method: 'GET',
      path: '/certificates/{user_id}',
      description: 'Get Certificates by User ID',
      pathParams: [{ name: 'user_id', type: 'Integer', required: true, description: 'The ID of the user' }],
      response: { status: '200 OK', content: 'JSON array of user certificates' },
      exampleRequest: 'curl -X GET "https://crlndportal-backend-dev.salmonground-e485a14f.eastus.azurecontainerapps.io/api/v1/certificates/1"',
      exampleResponse: [
        {
          certificate_id: 1,
          course_id: 1,
          course_name: 'Introduction to Python',
          filename: 'cert_python.pdf',
          blob_name: 'certificates/cert_python.pdf',
          container_name: 'user-certs',
          issued_at: '2025-09-01T12:00:00',
        },
      ],
    },
  ];

  const navItems = [
    { id: 'introduction', title: 'Introduction' },
    { id: 'base-url', title: 'Base URL' },

    ...endpoints.map((ep, index) => ({ id: `endpoint-${index}`, title: `${ep.method} ${ep.path}` })),
    { id: 'error-handling', title: 'Error Handling' },
    { id: 'notes', title: 'Notes' },
  ];

  return (
    <div className="lndad-container">
      <div className="lndad-sidebar">
        <div className="lndad-sidebar-title">L&D APIs</div>
        <ul className="lndad-sidebar-list">
          {navItems.map((item) => (
            <li
              key={item.id}
              className={`lndad-sidebar-item ${activeId === item.id ? 'lndad-sidebar-item-active' : ''}`}
              onClick={() => navigateTo(item.id)}
            >
              {item.title}
            </li>
          ))}
        </ul>
      </div>
      <div className="lndad-main">
        <h1 className="lndad-header">Learning & Development API Documentation</h1>

        <Section title="Introduction" id="introduction">
          <p>This API provides endpoints for managing courses, user enrollments, progress tracking, and user insights in an educational platform.</p>
        </Section>

        <Section title="Base URL" id="base-url">
          <p>https://crlndportal-backend-dev.salmonground-e485a14f.eastus.azurecontainerapps.io/api/v1</p>
        </Section>

        <Section title="APIs" id="authentication">

        </Section>

        {endpoints.map((ep, index) => (
          <div id={`endpoint-${index}`} key={index}>
            <Endpoint {...ep} />
          </div>
        ))}

        <Section title="Error Handling" id="error-handling">
          <p>All endpoints handle errors gracefully and return appropriate HTTP status codes:</p>
          <ul>
            <li><strong>400 Bad Request</strong>: Invalid input parameters or request body.</li>
            <li><strong>404 Not Found</strong>: Resource (e.g., user, course) not found.</li>
            <li><strong>500 Internal Server Error</strong>: Unexpected server error.</li>
          </ul>
          <h4>Example Error Response</h4>
          <div className="lndad-code-block">
            <pre>
              <code>{JSON.stringify({ detail: 'User is not enrolled in this course' }, null, 2)}</code>
            </pre>
            <button
              className="lndad-copy-btn"
              onClick={() => navigator.clipboard.writeText(JSON.stringify({ detail: 'User is not enrolled in this course' }, null, 2))}
            >
              Copy
            </button>
          </div>
        </Section>

      </div>
    </div>
  );
};

APIDocumentation.propTypes = {};

export default APIDocumentation;